import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Tooltip from '@/components/atoms/Tooltip';
import { formatDateShortLocalized } from '@/utils/date';

import { laneGapPx } from '../../helpers/laneSpacing';
import type { FocusSource } from '../../helpers/lineFocus';
import { trackStyle } from '../../helpers/trackStyle';
import { transferAmount, transferLabel } from '../../helpers/transferFacts';
import type { TransferSegment } from '../../types';

import styles from './TransferLines.module.scss';

interface TransferLinesProps {
    segments: TransferSegment[] | undefined;
    /**
     * The lines lit on every row they cross: the one under the pointer or focus, or the ones
     * into a hovered or focused group. Null while none is.
     */
    activeKeys: ReadonlySet<string> | null;
    /**
     * A line under the pointer, or its end under keyboard focus, by key; null once it leaves.
     * The pointer and the keyboard are reported separately, so one leaving doesn't clear the
     * line the other still holds.
     */
    onActiveChange: (source: FocusSource, key: string | null) => void;
    /** One day's width on the track as drawn, which an operation's fan stays within. */
    dayPx?: number;
}

/**
 * A line's place in its operation's fan. An end button is 12px, or the gap when the fan is
 * tighter, so two ends in one row never overlap.
 */
function laneStyle(segment: TransferSegment, dayPx: number) {
    const gap = laneGapPx(segment.lanes, dayPx);
    return {
        ...trackStyle(segment.leftPct),
        '--lane': String(segment.lane),
        '--lane-gap': `${gap}px`,
        '--end-size': `${segment.lanes > 1 ? Math.min(END_PX, gap) : END_PX}px`,
    };
}

const END_PX = 12;

/**
 * This row's pieces of the lines that join each transfer's source to its destination. A line
 * is plain until the pointer or focus is on it: hovering any piece, or hovering or focusing its
 * destination end, lights the whole line and shows its count and share. The destination end is
 * a small button, so a keyboard reaches every transfer and a screen reader hears its groups,
 * date and count.
 */
export default function TransferLines(props: TransferLinesProps) {
    const { segments = [], activeKeys, onActiveChange, dayPx = 0 } = props;
    const { t, i18n } = useTranslation('fishGroups');
    if (segments.length === 0) return null;
    const locale = i18n.language;
    const point = (key: string | null) => () => onActiveChange('pointer', key);
    // While a line is active it is drawn above every bar, badge and other line on the board
    // (its row's layers are raised), and the other lines fade.
    const isActive = (segment: TransferSegment) => activeKeys?.has(segment.key) ?? false;
    const holdsActive = segments.some(isActive);
    const stateOf = (segment: TransferSegment) =>
        activeKeys === null ? 'plain' : isActive(segment) ? 'active' : 'dimmed';

    return (
        <>
            {/* Pointer only: the destination buttons below carry the same text for keys. */}
            <div
                className={classNames(styles.layer, { [styles.layerActive]: holdsActive })}
                aria-hidden="true">
                {segments.map((segment) => (
                    <span
                        key={segment.key}
                        data-line={stateOf(segment)}
                        data-line-key={segment.key}
                        className={classNames(
                            styles.lineSpot,
                            styles[`lineSpot--${segment.part}`],
                            styles[`lineSpot--${stateOf(segment)}`]
                        )}
                        style={laneStyle(segment, dayPx)}
                        onMouseEnter={point(segment.key)}
                        onMouseLeave={point(null)}>
                        <Tooltip
                            content={transferLabel(segment.transfer, locale, t)}
                            layout="fill"
                            targetClassName={styles.lineHit}>
                            <span className={styles.line} />
                        </Tooltip>
                    </span>
                ))}
                {segments
                    .filter((segment) => segment.endpoint === 'source' && isActive(segment))
                    .map((segment) => (
                        <span
                            key={`${segment.key}-source`}
                            className={styles.sourceRing}
                            style={laneStyle(segment, dayPx)}
                        />
                    ))}
            </div>
            <div className={classNames(styles.layer, { [styles.layerActive]: holdsActive })}>
                {segments
                    .filter((segment) => segment.endpoint === 'destination')
                    .map((segment) => {
                        const { transfer } = segment;
                        const label = transferLabel(transfer, locale, t);
                        return (
                            <span
                                key={segment.key}
                                data-line={stateOf(segment)}
                                data-line-key={segment.key}
                                className={classNames(
                                    styles.handleSpot,
                                    styles[`handleSpot--${stateOf(segment)}`]
                                )}
                                style={laneStyle(segment, dayPx)}>
                                <Tooltip content={label} layout="fit">
                                    <button
                                        type="button"
                                        className={styles.handle}
                                        aria-label={t('board.transfer.name', {
                                            date: formatDateShortLocalized(transfer.date, locale),
                                            source: transfer.sourceName,
                                            destination: transfer.destinationName,
                                            amount: transferAmount(transfer, locale, t),
                                        })}
                                        onFocus={() => onActiveChange('keyboard', segment.key)}
                                        onBlur={() => onActiveChange('keyboard', null)}
                                        onMouseEnter={point(segment.key)}
                                        onMouseLeave={point(null)}
                                    />
                                </Tooltip>
                            </span>
                        );
                    })}
            </div>
        </>
    );
}
